import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { WayfindingService } from '../../core/wayfinding/wayfinding.service';
import { WayfindingPlay, WayfindingWorkflowMatch } from '../../core/wayfinding/wayfinding.models';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { WorkflowApiService, WorkflowIntentOpsDetail } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    RouterLink,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    TableModule,
    TagModule,
    DatePipe
  ],
  template: `
    <ain-page [title]="t('workflow.intents.detail.title')" [description]="t('workflow.intents.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'workflow.intents.detail.hint.title'"
        [descriptionKey]="'workflow.intents.detail.hint.description'"
        [bulletKeys]="[
          'workflow.intents.detail.hint.bullets.status',
          'workflow.intents.detail.hint.bullets.screens',
          'workflow.intents.detail.hint.bullets.next'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error() && !detail()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else if (detail(); as item) {
        @if (error()) {
          <p class="submit-error">{{ error() }}</p>
        }

        <section class="hero">
          <p class="prompt">{{ item.intent.envelope.text }}</p>
          <p-tag [value]="statusLabel(item.intent.status)" [severity]="statusSeverity(item.intent.status)" />
        </section>

        @if (needsNextStep()) {
          <section class="next">
            <h3>{{ t('workflow.intents.detail.next.title') }}</h3>
            <p>{{ nextStepCopy() }}</p>

            @if (suggestedPlay(); as play) {
              <article class="journey">
                <header class="journey-head">
                  <div>
                    <h4>{{ t(play.titleKey) }}</h4>
                    <p>{{ t(play.descriptionKey) }}</p>
                  </div>
                  <a pButton [routerLink]="startRoute(play)" icon="pi pi-play" [label]="t('wayfinding.startPath')"></a>
                </header>
                <ol class="path">
                  @for (step of requiredSteps(play); track step.route; let idx = $index; let last = $last) {
                    <li>
                      <a class="step" [routerLink]="step.route">
                        <span class="step-index">{{ idx + 1 }}</span>
                        <strong>{{ t(step.titleKey) }}</strong>
                        <small>{{ t(step.whyKey) }}</small>
                      </a>
                    </li>
                    @if (!last) {
                      <li class="path-join" aria-hidden="true">
                        <i class="pi" [class]="isRtl() ? 'pi-arrow-left' : 'pi-arrow-right'"></i>
                      </li>
                    }
                  }
                </ol>
              </article>
            }

            @for (workflow of suggestedWorkflows(); track workflow.id) {
              <a class="workflow-chip" [routerLink]="workflow.route">
                <i class="pi pi-sitemap" aria-hidden="true"></i>
                {{ workflow.name }}
              </a>
            }

            @if (hasClarificationSlots()) {
              @if (isIntentOnlyClarification()) {
                <label for="intent-refine">{{ t('workflow.intents.detail.refine.label') }}</label>
                <textarea
                  id="intent-refine"
                  pTextarea
                  rows="4"
                  [(ngModel)]="clarificationValues['intent']"></textarea>
                <small class="hint">{{ t('workflow.intents.detail.refine.hint') }}</small>
              } @else {
                <div class="clarification-grid">
                  @for (slot of clarificationSlots(); track slot) {
                    <div class="field">
                      <label>{{ slotLabel(slot) }}</label>
                      <input pInputText [(ngModel)]="clarificationValues[slot]" />
                    </div>
                  }
                </div>
                <small class="hint">{{ t('workflow.intents.detail.hints.clarification') }}</small>
              }
              <div class="next-actions">
                <button
                  pButton
                  type="button"
                  icon="pi pi-refresh"
                  [label]="t('workflow.intents.detail.actions.replan')"
                  [loading]="replanning()"
                  [disabled]="replanning()"
                  (click)="submitClarificationReplan()"></button>
              </div>
            }
          </section>
        }

        @if (candidateRows().length > 0) {
          <section class="candidates">
            <h3>{{ t('workflow.intents.detail.sections.candidates') }}</h3>
            @if (canStartInstance()) {
              <p>{{ t('workflow.intents.detail.next.ready') }}</p>
            }
            <ul class="candidate-list">
              @for (candidate of candidateRows(); track candidate.id) {
                <li [class.selected-candidate]="candidate.isSelected">
                  @if (candidate.processDefinitionId) {
                    <a class="candidate-name" [routerLink]="['/workflow/definitions', candidate.processDefinitionId]">{{ candidate.displayName }}</a>
                  } @else {
                    <strong>{{ candidate.displayName }}</strong>
                  }
                  <p-tag
                    [value]="candidate.isSelected ? t('workflow.intents.detail.candidates.selected') : t('workflow.intents.detail.candidates.notSelected')"
                    [severity]="candidate.isSelected ? 'success' : 'secondary'" />
                </li>
              }
            </ul>
            <div class="next-actions">
              @if (canStartInstance()) {
                <button
                  pButton
                  type="button"
                  icon="pi pi-play"
                  [label]="t('workflow.startInstance')"
                  [loading]="starting()"
                  [disabled]="starting()"
                  (click)="startSelectedInstance()"></button>
              }
              @if (startedInstanceId(); as instanceId) {
                <a pButton [routerLink]="['/workflow/instances', instanceId]" icon="pi pi-external-link" [label]="t('workflow.intents.detail.actions.openInstance')"></a>
              }
            </div>
          </section>
        }

        @if (primaryActions().length > 0) {
          <div class="actions">
            @for (action of primaryActions(); track action) {
              <button pButton type="button" severity="secondary" [outlined]="true" [label]="tAction(action)" (click)="openActionDialog(action)"></button>
            }
          </div>
        }

        @if (item.timeline.length > 0) {
          <section class="history">
            <h3>{{ t('workflow.intents.detail.sections.timeline') }}</h3>
            <ol>
              @for (event of item.timeline; track event.occurredAtUtc + event.eventType) {
                <li>
                  <time>{{ event.occurredAtUtc | date: 'short' }}</time>
                  <span>{{ timelineActionLabel(event.eventType) }}</span>
                  <span>{{ timelineOutcomeLabel(event.approvalStatus, event.executionStatus, event.code) }}</span>
                </li>
              }
            </ol>
          </section>
        }

        <button type="button" class="advanced-toggle" (click)="showAdvanced.set(!showAdvanced())">
          {{ t('workflow.intents.detail.advanced.toggle') }}
          <i class="pi" [class]="showAdvanced() ? 'pi-chevron-up' : 'pi-chevron-down'" aria-hidden="true"></i>
        </button>

        @if (showAdvanced()) {
          <section class="advanced" [attr.aria-label]="t('workflow.intents.detail.advanced.toggle')">
            <div class="kv"><strong>{{ t('workflow.intents.ops.columns.intentKey') }}:</strong> {{ intentDisplay(item.intent.draftPlan.intentKey) }}</div>
            <div class="kv"><strong>{{ t('workflow.intents.ops.columns.approvalStatus') }}:</strong> {{ statusLabel(item.intent.approvalStatus) }}</div>
            <div class="kv"><strong>{{ t('workflow.intents.ops.columns.executionStatus') }}:</strong> {{ statusLabel(item.intent.executionStatus) }}</div>
            @if (item.intent.processInstanceId) {
              <div class="kv"><strong>{{ t('workflow.intents.detail.fields.processInstanceId') }}:</strong> {{ item.intent.processInstanceId }}</div>
            }
            @if (resolveLastError() !== '-') {
              <div class="kv"><strong>{{ t('workflow.intents.detail.fields.lastError') }}:</strong> {{ resolveLastError() }}</div>
            }
            <div class="kv"><strong>{{ t('workflow.intents.detail.fields.channel') }}:</strong> {{ item.intent.envelope.channel }}</div>
            <div class="kv"><strong>{{ t('workflow.intents.detail.fields.selectedTarget') }}:</strong> {{ item.intent.draftPlan.targetType }} / {{ item.intent.draftPlan.targetKey || '-' }}</div>

            <div class="raw-json-grid">
              <div class="raw-json">
                <strong>{{ t('workflow.intents.detail.fields.selectedCandidateJson') }}</strong>
                <pre>{{ item.intent.draftPlan.selectedCandidateJson }}</pre>
              </div>
              <div class="raw-json">
                <strong>{{ t('workflow.intents.detail.fields.candidatesJson') }}</strong>
                <pre>{{ item.intent.draftPlan.candidatesJson }}</pre>
              </div>
            </div>

            @if (advancedActions().length > 0) {
              <div class="actions">
                @for (action of advancedActions(); track action) {
                  <button pButton type="button" size="small" [label]="tAction(action)" (click)="openActionDialog(action)"></button>
                }
              </div>
            }

            <h4>{{ t('workflow.intents.feedback.title') }}</h4>
            <div class="feedback-grid">
              <div class="field">
                <label>{{ t('workflow.intents.feedback.fields.intentCorrect') }}</label>
                <p-select appendTo="body" [options]="intentCorrectOptions" optionLabel="label" optionValue="value" [(ngModel)]="feedbackIntentCorrect" [ngModelOptions]="{standalone: true}"></p-select>
              </div>
              <div class="field">
                <label>{{ t('workflow.intents.feedback.fields.correctedIntentKey') }}</label>
                <input pInputText [(ngModel)]="feedbackCorrectedIntentKey" [ngModelOptions]="{standalone: true}" />
              </div>
              <div class="field">
                <label>{{ t('workflow.intents.feedback.fields.safetyExpectedDecision') }}</label>
                <input pInputText [(ngModel)]="feedbackSafetyExpectedDecision" [ngModelOptions]="{standalone: true}" />
              </div>
              <div class="field full">
                <label>{{ t('workflow.intents.feedback.fields.correctedSlotsJson') }}</label>
                <textarea pTextarea rows="4" [(ngModel)]="feedbackCorrectedSlotsJson" [ngModelOptions]="{standalone: true}"></textarea>
              </div>
              <div class="field full">
                <label>{{ t('workflow.intents.feedback.fields.notes') }}</label>
                <textarea pTextarea rows="3" [(ngModel)]="feedbackNotes" [ngModelOptions]="{standalone: true}"></textarea>
              </div>
            </div>
            <div class="feedback-actions">
              <button pButton type="button" [label]="t('workflow.intents.feedback.actions.submit')" [loading]="feedbackSubmitting()" (click)="submitFeedback()"></button>
              @if (feedbackSubmitted()) {
                <span class="hint">{{ t('workflow.intents.feedback.states.submitted') }}</span>
              }
            </div>

            <h4>{{ t('workflow.intents.detail.sections.timeline') }}</h4>
            <p-table [value]="item.timeline">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('columns.date') }}</th>
                  <th>{{ t('workflow.intents.timeline.columns.action') }}</th>
                  <th>{{ t('workflow.intents.timeline.columns.outcome') }}</th>
                  <th>{{ t('workflow.intents.timeline.columns.actor') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.occurredAtUtc | date: 'short' }}</td>
                  <td>{{ timelineActionLabel(row.eventType) }}</td>
                  <td>{{ timelineOutcomeLabel(row.approvalStatus, row.executionStatus, row.code) }}</td>
                  <td>{{ timelineActorLabel(row.actorUserId) }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
      }

      <p-dialog [(visible)]="actionDialogVisible" [header]="tAction(selectedAction)" [modal]="true" [style]="{ width: '32rem' }">
        <div class="dialog-body">
          <label>{{ t('workflow.intents.detail.fields.reason') }}</label>
          <textarea pTextarea rows="4" [(ngModel)]="reason" [ngModelOptions]="{standalone: true}"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="actionDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="runAction()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .hero, .next, .candidates, .history, .advanced { display: grid; gap: 12px; margin-bottom: 20px; }
    .hero { align-items: start; }
    .prompt {
      margin: 0;
      font-size: 1.15rem;
      line-height: 1.7;
      font-weight: 600;
    }
    h3, h4 { margin: 0; font-size: 1.05rem; }
    .hint { display: block; color: #6b7280; }
    .submit-error { color: #dc2626; margin: 0 0 12px; }
    .journey {
      display: grid;
      gap: 12px;
      padding: 16px;
      border-radius: 16px;
      border: 1px solid #c7d2fe;
      background: linear-gradient(160deg, #eef2ff 0%, #ffffff 42%);
    }
    .journey-head { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; justify-content: space-between; }
    .journey-head p { margin: 4px 0 0; color: #64748b; }
    .path {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: stretch;
      gap: 8px;
    }
    .path li { display: flex; align-items: stretch; }
    .path-join { align-items: center !important; color: #6366f1; }
    .step {
      display: grid;
      gap: 6px;
      min-width: 148px;
      max-width: 220px;
      padding: 12px;
      border-radius: 14px;
      background: white;
      border: 1px solid #e0e7ff;
      text-decoration: none;
      color: inherit;
    }
    .step-index {
      width: 22px;
      height: 22px;
      border-radius: 999px;
      background: #4f46e5;
      color: white;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .step strong { font-size: 13px; }
    .step small { color: #64748b; line-height: 1.35; }
    .workflow-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      width: fit-content;
      padding: 8px 12px;
      border-radius: 999px;
      background: #ecfdf5;
      color: #166534;
      text-decoration: none;
      font-weight: 600;
    }
    .clarification-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
    .field { display: grid; gap: 6px; }
    .full { grid-column: 1 / -1; }
    .next-actions, .actions, .feedback-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .candidate-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
    .candidate-list li {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      padding: 10px 12px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
    }
    .selected-candidate { background: #f0fdf4; border-color: #86efac; }
    .candidate-name { font-weight: 700; color: inherit; text-decoration: none; }
    .candidate-name:hover { color: #4f46e5; }
    .history ol { margin: 0; padding: 0; list-style: none; display: grid; gap: 8px; }
    .history li { display: flex; gap: 10px; flex-wrap: wrap; color: #475569; }
    .advanced-toggle {
      border: 0;
      background: transparent;
      color: #64748b;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 0;
      margin-bottom: 12px;
    }
    .kv { display: flex; gap: 8px; flex-wrap: wrap; }
    .raw-json-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }
    .raw-json pre { margin: 6px 0 0; max-height: 160px; overflow: auto; padding: 8px; background: #f8fafc; border-radius: 6px; }
    .feedback-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
    .dialog-body { display: grid; gap: 8px; }
  `]
})
export class WorkflowIntentDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly wayfinding = inject(WayfindingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly detail = signal<WorkflowIntentOpsDetail | null>(null);
  readonly clarificationSlots = signal<string[]>([]);
  readonly candidateRows = signal<Array<{
    id: string;
    isSelected: boolean;
    sourceType: string;
    sourceId: string;
    targetKey: string;
    processDefinitionId: string;
    displayName: string;
    rank: number;
    score: number;
    reason: string;
    executabilityHint: string;
  }>>([]);
  readonly suggestedPlay = signal<WayfindingPlay | null>(null);
  readonly suggestedWorkflows = signal<WayfindingWorkflowMatch[]>([]);
  readonly replanning = signal(false);
  readonly starting = signal(false);
  readonly feedbackSubmitting = signal(false);
  readonly feedbackSubmitted = signal(false);
  readonly showAdvanced = signal(false);
  readonly isRtl = computed(() => {
    const culture = this.localization.activeCulture().toLowerCase();
    return culture.startsWith('fa') || culture.startsWith('ar');
  });

  clarificationValues: Record<string, string> = {};
  feedbackIntentCorrect: 'true' | 'false' | 'unknown' = 'unknown';
  feedbackCorrectedIntentKey = '';
  feedbackCorrectedSlotsJson = '{}';
  feedbackSafetyExpectedDecision = '';
  feedbackNotes = '';

  selectedAction = '';
  reason = '';
  actionDialogVisible = false;

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
  }

  reload(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set(this.t('common.invalidRequest'));
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.api.getIntentOpsDetail(id).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: response => {
        this.detail.set(response);
        this.initializeClarification(response);
        this.initializeCandidates(response);
        this.loadSuggestions(response.intent.envelope.text);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  openActionDialog(action: string): void {
    this.selectedAction = action;
    this.reason = '';
    this.actionDialogVisible = true;
  }

  runAction(): void {
    const intentRequestId = this.detail()?.intent.intentRequestId;
    if (!intentRequestId || !this.selectedAction) {
      return;
    }

    this.api.runIntentOpsAction(intentRequestId, {
      action: this.selectedAction,
      idempotencyKey: crypto.randomUUID(),
      reason: this.reason || undefined
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.actionDialogVisible = false;
        this.reload();
      },
      error: () => {
        this.actionDialogVisible = false;
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  hasClarificationSlots(): boolean {
    return this.clarificationSlots().length > 0;
  }

  isIntentOnlyClarification(): boolean {
    const slots = this.clarificationSlots();
    return slots.length === 1 && slots[0].toLowerCase() === 'intent';
  }

  needsNextStep(): boolean {
    const detail = this.detail();
    if (!detail) {
      return false;
    }

    return this.isUnresolved()
      || detail.intent.status === 'NeedsClarification'
      || detail.intent.draftPlan.requiresClarification
      || this.hasClarificationSlots();
  }

  isUnresolved(): boolean {
    const plan = this.detail()?.intent.draftPlan;
    if (!plan) {
      return false;
    }

    if (plan.targetType === 'ProcessDefinition' && !!plan.targetKey) {
      return false;
    }

    const key = (plan.intentKey ?? '').toLowerCase();
    return key === 'unknown' || key === '' || plan.targetType === 'Unknown';
  }

  nextStepCopy(): string {
    return this.isUnresolved()
      ? this.t('workflow.intents.detail.next.unknown')
      : this.t('workflow.intents.detail.next.screens');
  }

  canStartInstance(): boolean {
    const detail = this.detail();
    return !!detail
      && !detail.intent.processInstanceId
      && detail.intent.status === 'ReadyForConfirmation'
      && this.selectedProcessDefinitionId().length > 0;
  }

  selectedProcessDefinitionId(): string {
    const selected = this.candidateRows().find(item => item.isSelected);
    if (selected?.processDefinitionId) {
      return selected.processDefinitionId;
    }

    const plan = this.detail()?.intent.draftPlan;
    return plan?.targetType === 'ProcessDefinition' ? (plan.targetKey ?? '') : '';
  }

  startedInstanceId(): string | null {
    return this.detail()?.intent.processInstanceId ?? null;
  }

  startSelectedInstance(): void {
    const intentRequestId = this.detail()?.intent.intentRequestId;
    if (!intentRequestId || this.starting()) {
      return;
    }

    this.starting.set(true);
    this.error.set(null);
    this.api.confirmIntent(intentRequestId, {
      idempotencyKey: crypto.randomUUID(),
      userConfirmed: true
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.starting.set(false))
    ).subscribe({
      next: result => {
        if (result.executionErrorCode) {
          this.error.set(this.t('common.saveFailed'));
        }

        if (result.processInstanceId) {
          void this.router.navigate(['/workflow/instances', result.processInstanceId]);
          return;
        }

        this.reload();
      },
      error: () => {
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  primaryActions(): string[] {
    return (this.detail()?.eligibleActions ?? []).filter(action => action.toLowerCase() === 'cancel');
  }

  advancedActions(): string[] {
    return (this.detail()?.eligibleActions ?? []).filter(action => action.toLowerCase() !== 'cancel');
  }

  requiredSteps(play: WayfindingPlay) {
    return play.steps.filter(step => !step.optional);
  }

  startRoute(play: WayfindingPlay): string {
    return this.requiredSteps(play)[0]?.route ?? play.steps[0]?.route ?? '/dashboard';
  }

  get intentCorrectOptions() {
    return [
      { label: this.t('common.unknown'), value: 'unknown' as const },
      { label: this.t('common.yes'), value: 'true' as const },
      { label: this.t('common.no'), value: 'false' as const }
    ];
  }

  submitClarificationReplan(): void {
    const detail = this.detail();
    if (!detail) {
      return;
    }

    const missingSlots = this.clarificationSlots();
    const emptySlot = missingSlots.find(slot => !this.clarificationValues[slot]?.trim());
    if (emptySlot) {
      this.error.set(this.t('workflow.intents.detail.validation.clarificationSlotRequired'));
      return;
    }

    const clarificationMap = Object.fromEntries(
      missingSlots.map(slot => [slot, this.clarificationValues[slot].trim()])
    );
    const contextualPrompt = this.buildClarifiedPrompt(detail.intent.envelope.text, clarificationMap);
    const mergedContext = this.mergeClarificationContext(detail.intent.envelope.contextJson, clarificationMap);

    this.replanning.set(true);
    this.error.set(null);
    this.api.planIntent({
      text: contextualPrompt,
      channel: detail.intent.envelope.channel,
      locale: detail.intent.envelope.locale,
      conversationId: detail.intent.envelope.conversationId,
      correlationId: detail.intent.envelope.correlationId,
      contextJson: JSON.stringify(mergedContext)
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.replanning.set(false))
    ).subscribe({
      next: result => {
        void this.router.navigate(['/workflow/intents', result.intentRequestId]);
      },
      error: () => {
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  resolveLastError(): string {
    const timeline = this.detail()?.timeline ?? [];
    const lastError = timeline.find(item => item.code);
    return lastError?.code || '-';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  tAction(action: string): string {
    const key = `workflow.intents.actions.${action.toLowerCase()}`;
    const localized = this.localization.translate(key);
    return localized === key ? action : localized;
  }

  statusLabel(status: string): string {
    if (!status) {
      return '-';
    }

    const key = `workflow.intents.detail.status.${status.toLowerCase()}`;
    const localized = this.localization.translate(key);
    if (localized !== key) {
      return localized;
    }

    return this.timelineStatusLabel(status);
  }

  statusSeverity(status: string): 'success' | 'info' | 'warn' | 'secondary' | 'danger' {
    const normalized = status.toLowerCase();
    if (normalized === 'needsclarification') {
      return 'warn';
    }

    if (normalized.includes('fail') || normalized.includes('reject')) {
      return 'danger';
    }

    if (normalized.includes('complete') || normalized.includes('approved')) {
      return 'success';
    }

    return 'info';
  }

  intentDisplay(intentKey: string): string {
    const normalized = (intentKey ?? '').toLowerCase();
    if (!normalized || normalized === 'unknown') {
      return this.t('workflow.intents.detail.status.unresolved');
    }

    return intentKey;
  }

  slotLabel(slot: string): string {
    const normalized = slot.toLowerCase().replace(/ref$/, '').replace(/key$/, '');
    const key = `workflow.intents.detail.slots.${normalized}`;
    const localized = this.localization.translate(key);
    return localized === key ? slot : localized;
  }

  timelineActionLabel(eventType: string): string {
    const key = `workflow.intents.timeline.events.${eventType.toLowerCase()}`;
    const localized = this.localization.translate(key);
    return localized === key ? eventType : localized;
  }

  timelineOutcomeLabel(approvalStatus: string, executionStatus: string, code?: string): string {
    if (code) {
      return code;
    }

    if (approvalStatus && approvalStatus !== 'None') {
      return this.timelineStatusLabel(approvalStatus);
    }

    return this.timelineStatusLabel(executionStatus);
  }

  timelineActorLabel(actorUserId?: string): string {
    if (!actorUserId) {
      return this.t('workflow.intents.timeline.actor.system');
    }

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(actorUserId)) {
      return this.t('workflow.intents.timeline.actor.user');
    }

    return actorUserId;
  }

  submitFeedback(): void {
    const intentRequestId = this.detail()?.intent.intentRequestId;
    if (!intentRequestId) {
      return;
    }

    this.feedbackSubmitting.set(true);
    this.feedbackSubmitted.set(false);
    this.error.set(null);
    this.api.submitIntentFeedback(intentRequestId, {
      isIntentCorrect: this.feedbackIntentCorrect === 'unknown'
        ? undefined
        : this.feedbackIntentCorrect === 'true',
      correctedIntentKey: this.feedbackCorrectedIntentKey || undefined,
      correctedSlotsJson: this.feedbackCorrectedSlotsJson || undefined,
      safetyExpectedDecision: this.feedbackSafetyExpectedDecision || undefined,
      notes: this.feedbackNotes || undefined
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.feedbackSubmitting.set(false))
    ).subscribe({
      next: () => {
        this.feedbackSubmitted.set(true);
      },
      error: () => {
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  private loadSuggestions(text: string): void {
    this.wayfinding.ensureDefinitionsLoaded(true).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const found = this.wayfinding.search(text);
      this.suggestedPlay.set(found.plays.find(play => !this.isSelfPlay(play)) ?? null);
      this.suggestedWorkflows.set(found.workflows);
      this.candidateRows.update(rows => rows.map(row => ({
        ...row,
        displayName: this.looksLikeGuid(row.displayName) || row.displayName === this.t('workflow.intents.detail.sections.candidates')
          ? (this.wayfinding.publishedDefinitionName(row.processDefinitionId) || row.displayName)
          : row.displayName
      })));
    });
  }

  private isSelfPlay(play: WayfindingPlay): boolean {
    const first = play.steps.find(step => !step.optional) ?? play.steps[0];
    return (first?.route ?? '').startsWith('/workflow/intents');
  }

  private initializeClarification(detail: WorkflowIntentOpsDetail): void {
    const slots = this.readStringArray(detail.intent.draftPlan.missingSlotsJson);
    this.clarificationSlots.set(slots);
    this.clarificationValues = Object.fromEntries(slots.map(slot => [
      slot,
      slot.toLowerCase() === 'intent' ? detail.intent.envelope.text : ''
    ]));
  }

  private initializeCandidates(detail: WorkflowIntentOpsDetail): void {
    const selected = this.readObject(detail.intent.draftPlan.selectedCandidateJson);
    const selectedId = this.resolveCandidateId(selected);
    const rows = this.readArray(detail.intent.draftPlan.candidatesJson)
      .map((item, index) => {
        const id = this.resolveCandidateId(item);
        const processDefinitionId = this.readString(item, 'processDefinitionId')
          || (this.readString(item, 'targetType') === 'ProcessDefinition' ? this.readString(item, 'targetKey') : '');
        const displayName = this.candidateDisplayName(item, processDefinitionId);
        const rank = index + 1;
        return {
          id,
          isSelected: selectedId !== '' && selectedId === id,
          sourceType: this.readString(item, 'sourceType', '-'),
          sourceId: this.resolveSourceId(item),
          targetKey: this.readString(item, 'targetKey', '-'),
          processDefinitionId,
          displayName,
          rank,
          score: this.readNumber(item, 'score'),
          reason: this.readString(item, 'reason', '-'),
          executabilityHint: this.readString(item, 'executabilityHint', '-')
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return `${a.sourceType}:${a.targetKey}`.localeCompare(`${b.sourceType}:${b.targetKey}`);
      })
      .map((item, index) => ({ ...item, rank: index + 1 }));

    this.candidateRows.set(rows);
  }

  private readStringArray(raw: string): string[] {
    const parsed = this.safeParse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(item => typeof item === 'string')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }

  private readArray(raw: string): Record<string, unknown>[] {
    const parsed = this.safeParse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(item => item && typeof item === 'object') as Record<string, unknown>[];
  }

  private readObject(raw: string): Record<string, unknown> | null {
    const parsed = this.safeParse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as Record<string, unknown>;
  }

  private safeParse(raw: string): unknown {
    if (!raw || raw === 'null') {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private candidateDisplayName(candidate: Record<string, unknown>, processDefinitionId: string): string {
    const named = this.readString(candidate, 'processDefinitionName')
      || this.readString(candidate, 'templateKey')
      || this.readString(candidate, 'actionKey');
    if (named && !this.looksLikeGuid(named)) {
      return named;
    }

    const suggested = this.suggestedWorkflows().find(item => item.id === processDefinitionId)?.name
      || this.wayfinding.publishedDefinitionName(processDefinitionId);
    if (suggested) {
      return suggested;
    }

    const targetKey = this.readString(candidate, 'targetKey');
    if (targetKey && !this.looksLikeGuid(targetKey)) {
      return targetKey;
    }

    return this.t('workflow.intents.detail.sections.candidates');
  }

  private looksLikeGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  private resolveCandidateId(candidate: Record<string, unknown> | null): string {
    if (!candidate) {
      return '';
    }

    const sourceType = this.readString(candidate, 'sourceType');
    const targetKey = this.readString(candidate, 'targetKey');
    const candidateType = this.readString(candidate, 'candidateType');
    return `${candidateType}|${sourceType}|${targetKey}`;
  }

  private resolveSourceId(candidate: Record<string, unknown>): string {
    const processDefinitionId = this.readString(candidate, 'processDefinitionId');
    if (processDefinitionId) {
      return processDefinitionId;
    }

    const templateKey = this.readString(candidate, 'templateKey');
    if (templateKey) {
      return templateKey;
    }

    const actionKey = this.readString(candidate, 'actionKey');
    if (actionKey) {
      return actionKey;
    }

    return this.readString(candidate, 'targetKey', '-');
  }

  private readString(candidate: Record<string, unknown>, key: string, fallback = ''): string {
    const value = candidate[key];
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  private readNumber(candidate: Record<string, unknown>, key: string): number {
    const value = candidate[key];
    return typeof value === 'number' ? value : 0;
  }

  private buildClarifiedPrompt(basePrompt: string, values: Record<string, string>): string {
    const entries = Object.entries(values);
    if (entries.length === 1 && entries[0][0].toLowerCase() === 'intent') {
      return entries[0][1].trim() || basePrompt.trim();
    }

    const additions = entries
      .map(([slot, value]) => `${this.mapSlotToPromptToken(slot)} ${value}`)
      .join(' ');
    return `${basePrompt.trim()} ${additions}`.trim();
  }

  private mapSlotToPromptToken(slot: string): string {
    const normalized = slot.toLowerCase();
    if (normalized.includes('customer')) {
      return 'customer';
    }

    if (normalized.includes('product')) {
      return 'product';
    }

    if (normalized.includes('claim')) {
      return 'claim';
    }

    if (normalized.includes('content')) {
      return 'content';
    }

    if (normalized.includes('process')) {
      return 'process';
    }

    return slot;
  }

  private mergeClarificationContext(rawContextJson: string, values: Record<string, string>): Record<string, unknown> {
    const context = this.safeParse(rawContextJson);
    const contextObject = context && typeof context === 'object' && !Array.isArray(context)
      ? context as Record<string, unknown>
      : {};
    return {
      ...contextObject,
      clarificationSlots: values
    };
  }

  private timelineStatusLabel(status: string): string {
    const key = `workflow.intents.timeline.status.${status.toLowerCase()}`;
    const localized = this.localization.translate(key);
    return localized === key ? status : localized;
  }
}
